import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert as MuiAlert, Box, Button, Chip, CircularProgress, IconButton, Paper, Snackbar,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CircleIcon from '@mui/icons-material/Circle'
import EditIcon from '@mui/icons-material/Edit'
import MemoryIcon from '@mui/icons-material/Memory'
import RefreshIcon from '@mui/icons-material/Refresh'
import RouterIcon from '@mui/icons-material/Router'
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt'
import PlaceIcon from '@mui/icons-material/Place'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer,
  Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts'
import { DeviceDetail, DeviceReadingPoint, getDevice, getDeviceReadings } from '../services/gateways'
import {
  BatteryIcon, DeviceLogsTable, EditDeviceDialog, TrendChip, batteryColor, formatDateTime, timeAgo,
} from '../components/devices/deviceCommon'
import { useSettings } from '../contexts/SettingsContext'

type RangeKey = '24h' | '7d' | '30d'
const RANGE_HOURS: Record<RangeKey, number> = { '24h': 24, '7d': 168, '30d': 720 }

const DeviceDetailsScreen: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { isRTL } = useSettings()
  const locale = i18n.language || 'en'
  // MUI icons don't mirror themselves; the back arrow must point right in RTL
  const backIcon = <ArrowBackIcon sx={{ transform: isRTL ? 'scaleX(-1)' : undefined }} />
  const navigate = useNavigate()
  const { macAddress } = useParams<{ macAddress: string }>()
  const mac = macAddress || ''

  const [device, setDevice] = useState<DeviceDetail | null>(null)
  const [readings, setReadings] = useState<DeviceReadingPoint[]>([])
  const [range, setRange] = useState<RangeKey>('7d')
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null)

  const load = useCallback(async () => {
    if (!mac) return
    setLoading(true)
    try {
      const [dev, reads] = await Promise.all([getDevice(mac), getDeviceReadings(mac, RANGE_HOURS[range])])
      setDevice(dev)
      setReadings(reads)
      setNotFound(false)
    } catch (e: any) {
      if (e?.response?.status === 404) setNotFound(true)
      else setToast({ msg: t('devices.toastLoadFailed'), sev: 'error' })
    } finally {
      setLoading(false)
    }
  }, [mac, range, t])

  useEffect(() => { load() }, [load])

  const chartData = useMemo(() => readings.map(r => ({
    ts: new Date(r.timestamp).getTime(),
    battery: r.batteryPercent,
    signal: r.signal,
  })), [readings])

  const tickFormatter = useCallback((ts: number) => {
    const d = new Date(ts)
    return range === '24h'
      ? d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })
  }, [range, locale])

  const labelFormatter = useCallback((ts: number) => new Date(ts).toLocaleString(locale), [locale])

  if (notFound) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={backIcon} onClick={() => navigate('/devices')}>{t('devices.backToDevices')}</Button>
        <MuiAlert severity="warning" sx={{ mt: 2 }}>{t('devices.deviceNotFound')}</MuiAlert>
      </Box>
    )
  }

  if (!device) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
  }

  const batteryNow = device.lastBatteryPercent

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/devices')} aria-label={t('devices.backToDevices')}>{backIcon}</IconButton>
        <MemoryIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {device.name || device.macAddress}
        </Typography>
        <Chip
          size="small"
          icon={<CircleIcon sx={{ fontSize: '12px !important', color: `${device.online ? '#43a047' : '#ef5350'} !important` }} />}
          label={device.online ? t('devices.statusOnline') : t('devices.statusOffline')}
        />
        <TrendChip trend={device.lastTrend} />
        <Box sx={{ flex: 1 }} />
        <Tooltip title={t('devices.edit')}>
          <IconButton onClick={() => setEditOpen(true)} aria-label={t('devices.edit')}><EditIcon /></IconButton>
        </Tooltip>
        <Button startIcon={<RefreshIcon />} onClick={load}>{t('devices.refresh')}</Button>
      </Box>

      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary', mb: 2, ml: 6 }}>
        {t('devices.colSerial')}: {device.macAddress}
      </Typography>

      {/* Info cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Battery card with sparkline */}
        <Paper sx={{ p: 2, minWidth: 240, flex: '1 1 240px', maxWidth: 360 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">{t('devices.battery')}</Typography>
            <BatteryIcon pct={batteryNow} fontSize={22} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: batteryColor(batteryNow) }}>
            {batteryNow != null ? `${batteryNow}%` : '—'}
          </Typography>
          <Box sx={{ height: 56, mt: 1 }}>
            {chartData.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="batterySpark" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={batteryColor(batteryNow)} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={batteryColor(batteryNow)} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <RTooltip
                    labelFormatter={labelFormatter}
                    formatter={(v: any) => [`${v}%`, t('devices.battery')]}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <XAxis dataKey="ts" hide type="number" domain={['dataMin', 'dataMax']} />
                  <YAxis hide domain={[0, 100]} />
                  <Area type="monotone" dataKey="battery" stroke={batteryColor(batteryNow)} strokeWidth={2} fill="url(#batterySpark)" isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="caption" color="text.secondary">{t('devices.noReadings')}</Typography>
            )}
          </Box>
        </Paper>

        <InfoCard
          icon={<SignalCellularAltIcon color="primary" />}
          label={t('devices.signal')}
          value={device.lastSignal != null ? `${device.lastSignal}/5` : '—'}
        />
        <InfoCard
          icon={<RouterIcon color="primary" />}
          label={t('devices.colGateway')}
          value={device.gatewayName || device.gatewayMacAddress}
          sub={device.gatewayMacAddress}
        />
        <InfoCard
          icon={device.yardName ? <PlaceIcon color="primary" /> : <WarningAmberIcon color="warning" />}
          label={t('devices.colSite')}
          value={device.yardName || t('devices.unassigned')}
        />
        <InfoCard
          label={t('devices.colLastTransmission')}
          value={timeAgo(device.lastSeenAt)}
          sub={formatDateTime(device.lastSeenAt)}
        />
        <InfoCard label={t('devices.readingCount')} value={String(device.readingCount)} sub={t('devices.firstSeenAt', { date: formatDateTime(device.firstSeenAt) })} />
        {device.sensorVersion && <InfoCard label={t('devices.version')} value={device.sensorVersion} />}
      </Box>

      {/* Range selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('devices.chartsTitle')}</Typography>
        <ToggleButtonGroup size="small" exclusive value={range} onChange={(_, v) => v && setRange(v)}>
          <ToggleButton value="24h">{t('devices.range24h')}</ToggleButton>
          <ToggleButton value="7d">{t('devices.range7d')}</ToggleButton>
          <ToggleButton value="30d">{t('devices.range30d')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, flex: '1 1 420px', minWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('devices.batteryOverTime')}</Typography>
          {loading ? <ChartLoader /> : chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="batteryMain" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#43a047" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#43a047" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="ts" type="number" domain={['dataMin', 'dataMax']} tickFormatter={tickFormatter} tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <RTooltip labelFormatter={labelFormatter} formatter={(v: any) => [`${v}%`, t('devices.battery')]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="battery" stroke="#43a047" strokeWidth={2} fill="url(#batteryMain)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </Paper>

        <Paper sx={{ p: 2, flex: '1 1 420px', minWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('devices.signalOverTime')}</Typography>
          {loading ? <ChartLoader /> : chartData.length > 1 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="ts" type="number" domain={['dataMin', 'dataMax']} tickFormatter={tickFormatter} tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 11 }} />
                <RTooltip labelFormatter={labelFormatter} formatter={(v: any) => [`${v}/5`, t('devices.signal')]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Line type="stepAfter" dataKey="signal" stroke="#1976d2" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </Paper>
      </Box>

      {/* Readings log */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{t('devices.logsTitle')}</Typography>
      <DeviceLogsTable macAddress={device.macAddress} />

      <EditDeviceDialog
        device={editOpen ? device : null}
        onClose={() => setEditOpen(false)}
        onSaved={name => { setEditOpen(false); setDevice({ ...device, name }) }}
        notify={(msg, sev) => setToast({ msg, sev })}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <MuiAlert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</MuiAlert> : undefined}
      </Snackbar>
    </Box>
  )
}

const InfoCard: React.FC<{ icon?: React.ReactNode; label: string; value: string; sub?: string }> = ({ icon, label, value, sub }) => (
  <Paper sx={{ p: 2, minWidth: 170, flex: '1 1 170px', maxWidth: 280 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {icon}
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{sub}</Typography>}
  </Paper>
)

const ChartLoader: React.FC = () => (
  <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={28} /></Box>
)

const EmptyChart: React.FC = () => {
  const { t } = useTranslation()
  return (
    <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
      {t('devices.noReadings')}
    </Box>
  )
}

export default DeviceDetailsScreen
